import { useCallback, useEffect, useState } from "react";
import { ACH_RETRY_TIMEOUT_MS, ACH_TIMEOUT_MS, fetchMyAchievements } from "../api/achievementsApi";
import type { AchievementsView } from "../model/types";
import { writeGoalShelf } from "./goalShelf";
import { achLang } from "./lang";

/**
 * Свои достижения для профиля, «Достижений» и строк-подсказок на других
 * экранах (AchievementGoal). Один ответ на язык живёт минуту: переход профиль →
 * «Все достижения» → «‹», и несколько подсказок на одной странице не спрашивают
 * сервер больше одного раза. Движок считает раз в ночь, так что свежее и не нужно.
 *
 * Готовый ответ отдаётся сразу, в первый кадр (и после минуты — пока идёт
 * новый запрос): «‹» назад и подсказки не ждут сети. Каждый свежий ответ
 * кладётся на полку подсказок (lib/goalShelf) — для первого кадра после
 * перезагрузки.
 *
 * Модуль лёгкий (ни каталога, ни картинок): его тянет и подсказка.
 */
const TTL_MS = 60_000;

type Entry = {
    key: string;
    at: number;
    promise: Promise<AchievementsView | null>;
    /** Ответ уже есть (этот или прошлый, пока идёт новый запрос) — value. */
    has: boolean;
    value?: AchievementsView | null;
};
let cache: Entry | null = null;

const token = (): string => {
    try {
        return localStorage.getItem("site_session_token") || "";
    } catch {
        return "";
    }
};

const cacheKey = (lang: string): string => `${token()}\n${achLang(lang)}`;

const load = (lang: string, force: boolean): Promise<AchievementsView | null> => {
    const key = cacheKey(lang);
    if (!force && cache && cache.key === key && Date.now() - cache.at < TTL_MS) return cache.promise;
    // force — «Повторить»: прошлый раз не дождались, теперь ждём дольше (ACH_RETRY_TIMEOUT_MS).
    const promise = fetchMyAchievements(lang, undefined, force ? ACH_RETRY_TIMEOUT_MS : ACH_TIMEOUT_MS);
    const prev = cache && cache.key === key && cache.has ? cache : null;
    const entry: Entry = { key, at: Date.now(), promise, has: !!prev, value: prev?.value };
    cache = entry;
    promise.then(
        (v) => {
            entry.has = true;
            entry.value = v;
            if (cache === entry) writeGoalShelf(lang, v);
        },
        () => {
            // Ошибку не храним: следующий заход спросит снова.
            if (cache === entry) cache = null;
        },
    );
    return promise;
};

/**
 * Уже пришедший ответ этого входа на этом языке (и устаревший — тоже) или
 * undefined: его ещё нет. Без запроса — для первого кадра.
 */
export const peekMyAchievements = (lang: string): AchievementsView | null | undefined =>
    cache && cache.has && cache.key === cacheKey(lang) ? cache.value : undefined;

/**
 * Тот же ответ без хука — для окна «за что эти баллы» (pointsPulse/lib/myPoints): оно
 * подгружает уровень заранее, до нажатия, и делит ответ (и минутный кеш) со строками целей.
 */
export const loadMyAchievements = (lang: string): Promise<AchievementsView | null> => load(lang, false);

/** Для тестов. */
export const resetMyAchievementsCache = () => {
    cache = null;
};

export type MyAchievements = {
    /** undefined — ещё грузится; null — достижений для этого человека нет (сервер: 404). */
    view: AchievementsView | null | undefined;
    failed: boolean;
    retry: () => void;
};

/**
 * enabled = false — ни одного запроса (не администратор, пока предпросмотр).
 * lang — язык страницы (первый сегмент адреса): на нём сервер пишет строки целей.
 */
export const useMyAchievements = (enabled = true, lang = "ru"): MyAchievements => {
    const [view, setView] = useState<AchievementsView | null | undefined>(() =>
        enabled ? peekMyAchievements(lang) : undefined,
    );
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!enabled) return;
        let alive = true;
        setFailed(false);
        load(lang, attempt > 0).then(
            (v) => {
                if (alive) setView(v);
            },
            () => {
                if (alive) setFailed(true);
            },
        );
        return () => {
            alive = false;
        };
    }, [enabled, attempt, lang]);

    const retry = useCallback(() => setAttempt((n) => n + 1), []);
    return { view, failed, retry };
};
